import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Prompt } from '../types';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import RelatedPrompts from './RelatedPrompts';

interface PromptDetailModalProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
  onCopy: (content: string) => void;
  onEdit?: () => void;
  onDelete?: (id: number) => void;
  allPrompts?: Prompt[]; // 新增：所有提示词数据用于相关推荐
  onPromptSelect?: (prompt: Prompt) => void; // 新增：选择其他提示词的回调
}

const PromptDetailModal: React.FC<PromptDetailModalProps> = ({ 
  prompt, 
  isOpen, 
  onClose, 
  onCopy,
  onEdit,
  onDelete,
  allPrompts = [], // 默认空数组
  onPromptSelect
}) => {
  const handleCopy = (content: string) => {
    onCopy(content);
    toast.success('提示词已复制到剪贴板！');
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const handleDelete = () => {
    if (prompt && onDelete && window.confirm('确定要删除这个提示词吗？此操作不可撤销。')) {
      onDelete(prompt.id);
      onClose();
    }
  };

  if (!prompt) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto transition-colors duration-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{prompt.title}</h3>
              <div className="flex items-center space-x-2">
                {onEdit && (
                  <button
                    onClick={handleEdit}
                    title="编辑提示词"
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={handleDelete}
                    title="删除提示词"
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
                <button
                  onClick={onClose}
                  title="关闭"
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* 分类和标签信息 */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                <i className="fas fa-folder mr-2"></i>
                {prompt.category}
              </span>
              {prompt.tags && prompt.tags.length > 0 && (
                <>
                  <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
                  <div className="flex flex-wrap gap-2">
                    {prompt.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary bg-opacity-10 text-primary border border-primary border-opacity-20"
                      >
                        <i className="fas fa-tag mr-1 text-xs"></i>
                        {tag}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">提示词内容</span>
                <button
                  onClick={() => handleCopy(prompt.content)}
                  className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <i className="fas fa-copy"></i>
                  <span>复制</span>
                </button>
              </div>
              <div className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                {prompt.content}
              </div>
            </div>

            {/* 效果展示图片 */}
            {prompt.images && prompt.images.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <i className="fas fa-images text-primary mr-2"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">效果展示</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({prompt.images.length}张)</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {prompt.images.map((imageUrl, index) => (
                    <motion.a
                      key={index}
                      href={imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="block aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-primary hover:shadow-lg transition-all group"
                    >
                      <img 
                        src={imageUrl} 
                        alt={`效果图 ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </motion.a>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 text-sm mb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 text-center"
              >
                <i className="fas fa-calendar-alt text-blue-500 text-lg mb-2"></i>
                <div className="text-blue-700 dark:text-blue-300 font-medium">创建时间</div>
                <div className="text-blue-600 dark:text-blue-400">{formatDate(prompt.createdAt)}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 text-center"
              >
                <i className="fas fa-text-width text-green-500 text-lg mb-2"></i>
                <div className="text-green-700 dark:text-green-300 font-medium">字符数</div>
                <div className="text-green-600 dark:text-green-400">{prompt.content.length}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 text-center"
              >
                <i className="fas fa-tags text-purple-500 text-lg mb-2"></i>
                <div className="text-purple-700 dark:text-purple-300 font-medium">标签数量</div>
                <div className="text-purple-600 dark:text-purple-400">{prompt.tags?.length || 0}</div>
              </motion.div>
            </div>

            {/* 相关提示词推荐 */}
            {allPrompts.length > 1 && (
              <RelatedPrompts
                currentPrompt={prompt}
                allPrompts={allPrompts}
                maxItems={4}
                onPromptClick={(relatedPrompt) => {
                  if (onPromptSelect) {
                    onPromptSelect(relatedPrompt);
                  }
                }}
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromptDetailModal; 